import type { Lang } from "@/lib/i18n";

export type ServiceSlug = "website" | "google-ads" | "seo" | "ai-seo";

type Bilingual = { nl: string; en: string };

export interface ServiceContent {
  slug: ServiceSlug;
  pathNl: string;
  pathEn: string;
  eyebrow: Bilingual;
  title: Bilingual;
  intro: Bilingual;
  // What you actually get
  deliverables: { nl: string[]; en: string[] };
  // Numbered approach / process
  approach: {
    nl: { title: string; desc: string }[];
    en: { title: string; desc: string }[];
  };
  // Optional case study block
  caseStudy?: {
    label: Bilingual;
    title: Bilingual;
    body: Bilingual;
    metrics: { value: string; label: Bilingual }[];
  };
  // Plain-language definitions
  glossary?: { term: string; def: Bilingual }[];
  // FAQ — citation-ready Q&A blocks (also emitted as FAQPage JSON-LD)
  faq?: { q: Bilingual; a: Bilingual }[];
  meta: {
    title: Bilingual;
    description: Bilingual;
  };
}

export const services: ServiceContent[] = [
  {
    slug: "website",
    pathNl: "/diensten/website",
    pathEn: "/en/services/website",
    eyebrow: {
      nl: "Website · Conversie & snelheid",
      en: "Website · Conversion & speed",
    },
    title: {
      nl: "Een website die laadt, scoort en verkoopt.",
      en: "A website that loads, ranks and sells.",
    },
    intro: {
      nl: "Je website is vaak het eerste wat een klant van je ziet. Is die traag of onduidelijk, dan haakt hij af en belt hij je concurrent. Wij bouwen een snelle site op maat die jouw bezoekers laat doen wat jij wil: bellen, mailen of een offerte aanvragen.",
      en: "Your website is often the first thing a customer sees. If it's slow or unclear, they bounce and call your competitor. We build a fast, tailor-made site that gets your visitors to do what you want: call, email or request a quote.",

    },
    deliverables: {
      nl: [
        "Maatwerk responsive design, geoptimaliseerd voor mobiel (50%+ van je verkeer)",
        "Snelle site speedscore richting 99 / 100",
        "Conversiegerichte landingspagina-templates, herbruikbaar per stad / segment",
        "SEO-fundament",
        "A/B-tests op bezoeker tot beller ratio",
        "Doorlopend onderhoud, contentupdates en maandelijkse publicatie van nieuwe pagina's",
      ],
      en: [
        "Custom responsive design, mobile-first (50%+ of your traffic)",
        "Fast frontend on lovable.dev, Lighthouse score approaching 99",
        "Conversion-focused landing page templates, reusable per city / segment",
        "On-page SEO foundation + built-in schema markup",
        "A/B tests on the visitor-to-call ratio",
        "Ongoing maintenance, content updates and monthly publication of new pages",
      ],
    },
    approach: {
      nl: [
        { title: "Doelgroep & doel", desc: "We starten met de basis: wie is je koper, wat moet de site doen (informeren, verkopen, leads genereren) en welk imago straal je uit." },
        { title: "Template & schema", desc: "Eén masterpagina, volledig gebouwd, schema ingebouwd, copy gestructureerd op zoekintentie. Daarna repliceren we per stad of dienst." },
        { title: "Bouwen op lovable.dev", desc: "Snelle frontend, clean code, eenvoudig schaalbaar. Pagina's gaan tot 10× sneller live dan op een klassiek WordPress-template." },
        { title: "Content & SEO", desc: "Wij schrijven of redigeren de teksten SEO- en AI-zoek-gereed." },
        { title: "Doorlopend optimaliseren", desc: "Flexibel pagina's bijbouwen, A/B-tests op CTA's, snelheid en formulieren. De site wordt elke maand sterker." },
      ],
      en: [
        { title: "Audience & goal", desc: "We start with the basics: who is your buyer, what should the site do (inform, sell, generate leads) and what image do you want to project." },
        { title: "Template & schema", desc: "One master page, fully built, schema included, copy structured for search intent. Then replicated per city or service." },
        { title: "Built on lovable.dev", desc: "Fast frontend, clean code, easy to scale. Pages go live up to 10× faster than on a classic WordPress template." },
        { title: "Content & SEO", desc: "We write or edit copy ready for both classic and AI search. No filler, just authority." },
        { title: "Continuous optimisation", desc: "Monthly page additions, A/B tests on CTAs, speed and forms. The site gets stronger every month." },
      ],
    },
    caseStudy: {
      label: { nl: "Case · FuelFixer", en: "Case · FuelFixer" },
      title: {
        nl: "Van WordPress score 46 naar lovable.dev 99 op SiteCheck.",
        en: "From WordPress 46 to lovable.dev 99 on SiteCheck.",
      },
      body: {
        nl: "Twee SiteCheck-runs, zelfde dag, zelfde condities. De WordPress-site van FuelFixer scoorde 46, onze lovable.dev-build scoorde 99. De huidige site laadt in 3,7 seconden, elke extra seconde boven de 1-seconde-benchmark kost je 7–10% van je bezoekers. Op ±1.000 bezoekers per maand betekent dat 150–250 mensen die afhaken vóórdat ze überhaupt iets kiezen. Ze bellen niet jou, ze bellen je concurrent. De nieuwe site is een eenmalige investering. Het huidige lek loopt elke maand door.",
        en: "Two SiteCheck runs, same day, same conditions. FuelFixer's WordPress site scored 46, our lovable.dev build scored 99. The current site loads in 3.7 seconds, every extra second above the 1-second benchmark costs you 7–10% of visitors. On ±1,000 monthly visitors that is 150–250 people gone before they even decide. They don't call you, they call your competitor. The new site is a one-off investment. The current leak compounds every month.",
      },
      metrics: [
        { value: "46 → 99", label: { nl: "Performance score (Lighthouse)", en: "Performance score (Lighthouse)" } },
        { value: "3,7s → <1s", label: { nl: "Laadtijd", en: "Load time" } },
        { value: "10×", label: { nl: "Sneller pagina's publiceren", en: "Faster to publish pages" } },
      ],
    },
    faq: [
      {
        q: { nl: "Hoe snel is een website van A-WAVE?", en: "How fast is an A-WAVE website?" },
        a: {
          nl: "Onze lovable.dev-builds halen een Lighthouse-score richting 99/100. Ter vergelijking: de WordPress-site van FuelFixer scoorde 46, onze build 99 — zelfde dag, zelfde condities. Elke seconde laadtijd boven de 1-seconde-benchmark kost je 7–10% van je bezoekers.",
          en: "Our lovable.dev builds score close to 99/100 on Lighthouse. For reference: FuelFixer's WordPress site scored 46, our build scored 99 — same day, same conditions. Every second of load time above the 1-second benchmark costs you 7–10% of visitors.",
        },
      },
      {
        q: { nl: "Wat krijg ik concreet bij een website?", en: "What do I actually get with a website?" },
        a: {
          nl: "Maatwerk responsive design (mobile-first, 50%+ van je verkeer), conversiegerichte landingspagina-templates die herbruikbaar zijn per stad of segment, SEO-fundament met schema, A/B-tests op de bezoeker-tot-beller ratio en doorlopend onderhoud met maandelijkse publicatie van nieuwe pagina's.",
          en: "Custom responsive design (mobile-first, 50%+ of your traffic), conversion-focused landing page templates reusable per city or segment, on-page SEO foundation with schema, A/B tests on the visitor-to-call ratio, and ongoing maintenance with monthly publication of new pages.",
        },
      },
      {
        q: { nl: "Hoeveel sneller kan ik nieuwe pagina's publiceren?", en: "How much faster can I publish new pages?" },
        a: {
          nl: "Tot 10× sneller dan op een klassiek WordPress-template. We bouwen één masterpagina met schema en gestructureerde copy, en repliceren die per stad of dienst.",
          en: "Up to 10× faster than on a classic WordPress template. We build one master page with schema and structured copy, then replicate it per city or service.",
        },
      },
      {
        q: { nl: "Waarom is laadsnelheid zo belangrijk?", en: "Why does load speed matter so much?" },
        a: {
          nl: "Bij ±1.000 bezoekers per maand betekent een laadtijd van 3,7s in plaats van <1s dat 150–250 mensen afhaken vóórdat ze iets kiezen. Ze bellen niet jou, ze bellen je concurrent.",
          en: "On ±1,000 monthly visitors, a load time of 3.7s instead of <1s means 150–250 people leave before they decide anything. They don't call you, they call your competitor.",
        },
      },
    ],
    meta: {
      title: {
        nl: "Website laten bouwen | Responsive, snel & SEO-proof | A-WAVE",
        en: "Custom website build | Responsive, fast & SEO-ready | A-WAVE",
      },
      description: {
        nl: "Responsive maatwerk-website op lovable.dev: Lighthouse 99, conversiegerichte landingspagina's en doorlopend onderhoud. Voor technische bedrijven die willen groeien.",
        en: "Custom responsive website on lovable.dev: Lighthouse 99, conversion-focused landing pages and ongoing maintenance. For technical companies that want to grow.",
      },
    },
  },
  {
    slug: "google-ads",
    pathNl: "/diensten/google-ads",
    pathEn: "/en/services/google-ads",
    eyebrow: {
      nl: "Google Ads · Meer kliks die klanten worden",
      en: "Google Ads · More clicks that become customers",
    },
    title: {
      nl: "Meer kliks die klanten worden.",
      en: "More clicks that become customers.",
    },
    intro: {
      nl: "Trek direct de juiste klanten aan met slimme, doelgerichte campagnes die jouw bedrijf bovenaan Google zetten. Transparant en meetbaar: je betaalt alleen voor geïnteresseerde bezoekers en ziet precies wat je advertentiebudget oplevert.",
      en: "Attract the right customers straight away with smart, targeted campaigns that put your business at the top of Google. Transparent and measurable: you only pay for interested visitors and see exactly what your ad budget delivers.",

    },

    deliverables: {
      nl: [
        "Account opgebouwd rond de zoekwoorden die echt klanten opleveren",
        "Slimme biedingen die sturen op winst, niet op clicks",
        "Campagnes gaan automatisch uit als je agenda vol zit",
        "Maandelijks rapport + sparringgesprek elke twee weken",
      ],
      en: [
        "Account rebuilt around the keywords that actually bring in customers",
        "Smart bidding that optimises for profit, not clicks",
        "Campaigns auto-pause when your schedule is full",
        "Monthly report + sparring call every two weeks",

      ],
    },
    approach: {
      nl: [
        { title: "Doorlichten", desc: "We checken je hele account: welke zoekwoorden leveren klanten op, welke kosten alleen geld?" },
        { title: "Opnieuw indelen", desc: "De toppers krijgen voorrang en eigen budget. Verliezers gaan uit of worden anders ingezet." },
        { title: "Waarde aan leads koppelen", desc: "Google leert hoeveel een telefoontje of aanvraag écht waard is en biedt daar slim op." },
        { title: "Automatisch aan- en uitzetten", desc: "Zit een stad of dag vol? Campagne stopt automatisch. Geen verspild budget aan leads die je niet kunt oppakken." },
        { title: "Breed starten, slim leren", desc: "We laten het algoritme eerst leren wie je beste klanten zijn, in plaats van mensen meteen uit te sluiten." },
      ],
      en: [
        { title: "Audit", desc: "We review your full account: which keywords bring in customers, which only cost money?" },
        { title: "Restructure", desc: "Top performers get priority and their own budget. Losers are paused or repurposed." },
        { title: "Value on every lead", desc: "Google learns what a call or request is actually worth and bids smartly on it." },
        { title: "Auto on and off", desc: "City or day fully booked? Campaign pauses automatically. No wasted spend on leads you can't service." },
        { title: "Start broad, learn smart", desc: "We let the algorithm learn who your best customers are first, instead of excluding people too early." },
      ],
    },
    caseStudy: {
      label: { nl: "Case · FuelFixer (DE)", en: "Case · FuelFixer (DE)" },
      title: {
        nl: "Van 770 keywords naar 146 high-performers, zonder leadverlies.",
        en: "From 770 keywords to 146 high-performers, without losing leads.",
      },
      body: {
        nl: "FuelFixer rijdt door heel Duitsland uit voor noodgevallen ('falsch getankt'). Het account telde 770 keywords met slechts 2.429 clicks en 142 conversies in 30 dagen, te gefragmenteerd om iets te analyseren. We bouwden het account om naar 3 performance-tiers met focus op de 146 best presterende keywords. Het topkeyword 'ausversehen benzin getankt' draaide al ROAS 36. Conversiewaardes werden gekoppeld aan calls en 'calls from ads', zodat het algoritme stuurt op marge in plaats van clicks. Daarbovenop bouwden we een capacity launcher die campagnes per stad automatisch uitschakelt zodra de planning vol zit, geen verbrand budget aan leads die niemand kan oppakken.",
        en: "FuelFixer drives across Germany for emergency mis-fuelling calls. The account had 770 keywords with only 2,429 clicks and 142 conversions in 30 days, too fragmented to analyse anything. We rebuilt the account into 3 performance tiers focused on the 146 best-performing keywords. The top keyword 'ausversehen benzin getankt' already ran at ROAS 36. Conversion values were attached to calls and 'calls from ads', so bidding optimises for margin instead of clicks. On top of that, we built a capacity launcher that auto-pauses campaigns per city the moment scheduling fills up, no burned budget on leads nobody can service.",
      },
      metrics: [
        { value: "770 → 146", label: { nl: "Keywords (gefocust op high-performers)", en: "Keywords (focused on high performers)" } },
        { value: "ROAS 36", label: { nl: "Topkeyword in DE", en: "Top keyword in DE" } },
        { value: "1 uur", label: { nl: "Update-interval capacity launcher", en: "Capacity launcher refresh" } },
        
      ],
    },
    faq: [
      {
        q: { nl: "Hoe pakken jullie een Google Ads-account aan?", en: "How do you approach a Google Ads account?" },
        a: {
          nl: "Doorlichten (welke zoekwoorden leveren klanten op, welke kosten alleen geld), opnieuw indelen in performance-tiers met eigen budget voor toppers, conversiewaardes koppelen aan calls en aanvragen, en een capacity launcher die campagnes per stad automatisch uitschakelt zodra de planning vol zit.",
          en: "Audit (which keywords bring in customers, which only cost money), restructure into performance tiers with dedicated budget for top performers, attach conversion values to calls and requests, and a capacity launcher that auto-pauses campaigns per city the moment scheduling fills up.",
        },
      },
      {
        q: { nl: "Welk resultaat haalden jullie voor FuelFixer?", en: "What results did you get for FuelFixer?" },
        a: {
          nl: "Het account ging van 770 gefragmenteerde keywords (2.429 clicks, 142 conversies in 30 dagen) naar 146 high-performers in 3 performance-tiers. Het topkeyword 'ausversehen benzin getankt' draaide ROAS 36. Conversiewaardes werden gekoppeld aan calls en 'calls from ads' zodat het algoritme stuurt op marge, niet clicks.",
          en: "The account moved from 770 fragmented keywords (2,429 clicks, 142 conversions in 30 days) to 146 high performers across 3 performance tiers. The top keyword 'ausversehen benzin getankt' ran at ROAS 36. Conversion values were attached to calls and 'calls from ads' so bidding optimises for margin, not clicks.",
        },
      },
      {
        q: { nl: "Wat gebeurt er als mijn agenda vol zit?", en: "What happens when my schedule is full?" },
        a: {
          nl: "De capacity launcher zet campagnes automatisch uit per stad of dag zodra de planning vol zit. Geen verspild budget aan leads die je niet kunt oppakken.",
          en: "The capacity launcher auto-pauses campaigns per city or day the moment scheduling fills up. No wasted budget on leads you can't service.",
        },
      },
      {
        q: { nl: "Hoe vaak rapporteren jullie?", en: "How often do you report?" },
        a: {
          nl: "Een maandelijks rapport plus een sparringgesprek elke twee weken.",
          en: "A monthly report plus a sparring call every two weeks.",
        },
      },
    ],
    meta: {
      title: {
        nl: "Google Ads-bureau voor meer kwalitatieve leads | A-WAVE",
        en: "Google Ads agency for more qualified leads | A-WAVE",
      },
      description: {
        nl: "Google Ads-rebuild in performance-tiers, conversiewaardes en capacity launcher. Meer leads, lagere CPL. Case: FuelFixer 770→146 keywords, ROAS 36.",
        en: "Google Ads rebuild in performance tiers, conversion values and capacity launcher. More leads, lower CPL. Case: FuelFixer 770→146 keywords, ROAS 36.",
      },
    },
  },
  {
    slug: "seo",
    pathNl: "/diensten/seo",
    pathEn: "/en/services/seo",
    eyebrow: { nl: "SEO · Organische groei die compoundt", en: "SEO · Organic growth that compounds" },
    title: {
      nl: "Technische SEO + content die je acquisitiekosten structureel verlaagt.",
      en: "Technical SEO + content that structurally lowers your acquisition cost.",
    },
    intro: {
      nl: "Je wil gevonden worden zonder elke klik te hoeven betalen. Wij zorgen dat jouw bedrijf bovenaan komt in Google op de zoekwoorden waar jouw klanten op zoeken. De eerste resultaten zie je na 6 tot 12 maanden, daarna komen er steeds meer aanvragen binnen zonder dat je advertentiebudget meegroeit.",
      en: "You want to be found without paying for every click. We get your business to the top of Google on the searches your customers actually use. First results show after 6 to 12 months — after that, more leads keep coming in without your ad budget growing.",

    },
    deliverables: {
      nl: [
        "Master landingspagina-template, schema ingebouwd, copy op zoekintentie",
        "4 stads-/segmentpagina's per maand live",
        "2 contentpagina's per maand op core keywords",
        "Minimaal 3–5 technische fixes per maand op prioriteit",
        "Schema-stack, canonical tags, hreflang, core web vitals",
        "Search Console-monitoring + maandelijks rapport",
        "Bi-weekly strategie-call (15–30 min)",
      ],
      en: [
        "Master landing page template, schema included, copy aligned to search intent",
        "4 city / segment pages live per month",
        "2 content pages per month on core keywords",
        "Minimum 3–5 technical fixes per month, prioritised by impact",
        "Schema stack, canonical tags, hreflang, core web vitals",
        "Search Console monitoring + monthly report",
        "Bi-weekly strategy call (15–30 min)",
      ],
    },
    approach: {
      nl: [
        { title: "Maand 1, Foundation", desc: "Google Business Profile-audit, competitor baseline, technische fixes, schema-stack, master template + eerste 2 stadspagina's en 2 core contentpagina's live." },
        { title: "Maand 2, Build", desc: "4 extra stadspagina's, 2 contentpagina's, core web vitals, homepage geoptimaliseerd voor AI search, eerste maandrapport." },
        { title: "Maand 3, Expand", desc: "Analyse omliggende landen (NL/BE/CH), internal linking-audit, structured data-audit, eerste ranking-signalen zichtbaar." },
        { title: "Maand 4–6", desc: "Eerste rankings in posities 11–30 op long-tail. Eerste organische calls. Uitbreiding naar omliggende markten." },
        { title: "Maand 6–12", desc: "Rankings consolideren op pagina 1 voor long-tail, meetbare bijdrage organische leads. Realistische competitie op core terms." },
      ],
      en: [
        { title: "Month 1, Foundation", desc: "Google Business Profile audit, competitor baseline, technical fixes, schema stack, master template + first 2 city pages and 2 core content pages live." },
        { title: "Month 2, Build", desc: "4 more city pages, 2 content pages, core web vitals, homepage rewritten for AI search, first monthly report." },
        { title: "Month 3, Expand", desc: "Analysis of neighbouring countries (NL/BE/CH), internal linking audit, structured data audit, first ranking signals visible." },
        { title: "Month 4–6", desc: "First rankings in positions 11–30 on long-tail. First organic calls. Expansion to neighbouring markets." },
        { title: "Month 6–12", desc: "Rankings consolidating to page 1 on long-tail, measurable organic lead contribution. Realistic competition on core terms." },
      ],
    },
    caseStudy: {
      label: { nl: "Case · FuelFixer (DE)", en: "Case · FuelFixer (DE)" },
      title: {
        nl: "Eigenaar worden van 'falsch getankt' in Duitsland.",
        en: "Own 'falsch getankt' in Germany.",
      },
      body: {
        nl: "Voor FuelFixer bouwen we een 90-dagen organisch fundament rond ~15.200 maandelijkse Duitse zoekopdrachten in de core-cluster. Eén master landingspagina-template dat zowel als organische pagina als paid-landingspagina dient, FuelFixer publiceert er 4 per maand. 6 autoriteitspagina's per zoekintentie (falsch getankt, benzin statt diesel, diesel statt benzin, auto tank auspumpen, adblue im kraftstoff). Vanaf maand 4 verwachten we de eerste rankings in posities 11–30 op long-tail, maand 6–9 eerste pagina, en realistische competitie op core terms tegen maand 9–12.",
        en: "For FuelFixer we're building a 90-day organic foundation around ~15,200 monthly German searches in the core cluster. One master landing page template that serves both as organic page and paid landing page, FuelFixer publishes 4 per month. 6 authority pages per search intent (falsch getankt, benzin statt diesel, diesel statt benzin, auto tank auspumpen, adblue im kraftstoff). From month 4 we expect first rankings in positions 11–30 on long-tail, page 1 by months 6–9, and realistic competition on core terms by months 9–12.",
      },
      metrics: [
        { value: "15,2K", label: { nl: "Maandelijkse DE-zoekopdrachten (core cluster)", en: "Monthly DE searches (core cluster)" } },
        { value: "20+", label: { nl: "Stadspagina's in 90 dagen", en: "City pages in 90 days" } },
        { value: "6 → 12 mo", label: { nl: "Organische groei-horizon", en: "Organic growth horizon" } },
      ],
    },
    glossary: [
      { term: "Schema markup", def: { nl: "Gestructureerde data die zoekmachines helpt je business, diensten en locaties beter te begrijpen.", en: "Structured data that helps search engines understand your business, services and locations." } },
      { term: "Core Web Vitals", def: { nl: "Google's officiële scores voor hoe snel en stabiel een pagina aanvoelt. Slechte scores verlagen je ranking.", en: "Google's official scores for how fast and stable a page feels. Poor scores lower your ranking." } },
      { term: "Canonical tags", def: { nl: "Signalen die zoekmachines vertellen welke versie van een pagina de primaire is.", en: "Signals telling search engines which version of a page is the primary one." } },
      { term: "Hreflang", def: { nl: "Technische tags die aangeven welke taal- of landversie van een pagina aan welke gebruiker getoond moet worden.", en: "Technical tags indicating which language or country version of a page to show to which user." } },
    ],
    faq: [
      {
        q: { nl: "Hoe lang duurt het voordat SEO resultaat oplevert?", en: "How long before SEO delivers results?" },
        a: {
          nl: "Eerste rankings in posities 11–30 op long-tail vanaf maand 4. Pagina 1 op long-tail rond maand 6–9. Realistische competitie op core terms tegen maand 9–12. Daarna komen er steeds meer aanvragen binnen zonder dat je advertentiebudget meegroeit.",
          en: "First rankings in positions 11–30 on long-tail from month 4. Page 1 on long-tail around months 6–9. Realistic competition on core terms by months 9–12. After that, more leads keep coming in without your ad budget growing.",
        },
      },
      {
        q: { nl: "Wat krijg ik maandelijks?", en: "What do I get monthly?" },
        a: {
          nl: "4 stads-/segmentpagina's live, 2 contentpagina's op core keywords, minimaal 3–5 technische fixes op prioriteit, schema-stack/canonical/hreflang/core web vitals onderhoud, Search Console-monitoring, maandrapport en een bi-weekly strategie-call van 15–30 min.",
          en: "4 city / segment pages live, 2 content pages on core keywords, minimum 3–5 technical fixes prioritised by impact, schema stack / canonical / hreflang / core web vitals maintenance, Search Console monitoring, monthly report and a bi-weekly strategy call of 15–30 min.",
        },
      },
      {
        q: { nl: "Wat gebeurt er in de eerste maand?", en: "What happens in month one?" },
        a: {
          nl: "Google Business Profile-audit, competitor baseline, technische fixes, schema-stack, master template live plus de eerste 2 stadspagina's en 2 core contentpagina's.",
          en: "Google Business Profile audit, competitor baseline, technical fixes, schema stack, master template live plus the first 2 city pages and 2 core content pages.",
        },
      },
      {
        q: { nl: "Voor welke landen werken jullie?", en: "Which countries do you work in?" },
        a: {
          nl: "Primair Nederland, België en Duitsland, met analyse van omliggende markten (NL/BE/CH) vanaf maand 3.",
          en: "Primarily the Netherlands, Belgium and Germany, with analysis of neighbouring markets (NL/BE/CH) from month 3.",
        },
      },
    ],
    meta: {
      title: {
        nl: "SEO-bureau voor technische bedrijven | A-WAVE",
        en: "SEO agency for technical companies | A-WAVE",
      },
      description: {
        nl: "Technische SEO + content die in 90 dagen het organische fundament legt. Stadspagina's, schema, core web vitals en autoriteit. Pay-off compoundt over 6–12 maanden.",
        en: "Technical SEO + content building the organic foundation in 90 days. City pages, schema, core web vitals and authority. Pay-off compounds over 6–12 months.",
      },
    },
  },
  {
    slug: "ai-seo",
    pathNl: "/diensten/ai-seo",
    pathEn: "/en/services/ai-seo",
    eyebrow: { nl: "AI SEO · Zichtbaar in ChatGPT, Perplexity & AI Overviews", en: "AI SEO · Visible in ChatGPT, Perplexity & AI Overviews" },
    title: {
      nl: "Word geciteerd door de AI's waar je kopers nu zoeken.",
      en: "Get cited by the AIs your buyers now search in.",
    },
    intro: {
      nl: "Steeds meer klanten vragen ChatGPT of Perplexity om advies in plaats van Google. Sta jij daar niet tussen de antwoorden, dan kiest je klant een ander. Wij zorgen dat jouw bedrijf wordt genoemd als de juiste keuze wanneer die vraag valt.",
      en: "More and more customers ask ChatGPT or Perplexity for advice instead of Google. If you're not in those answers, your customer picks someone else. We make sure your business gets mentioned as the right choice when that question comes up.",

    },
    deliverables: {
      nl: [
        "Check: hoe vaak word je nu genoemd in ChatGPT en Perplexity?",
        "Teksten herschreven zodat AI ze makkelijk kan oppakken",
        "Korte, duidelijke antwoorden bovenaan elke pagina",
        "FAQ-blokken die AI direct kan citeren",
        "Maandelijks rapport: waar word je genoemd en hoe?",
      ],
      en: [
        "Check: how often is your brand mentioned in ChatGPT and Perplexity today?",
        "Copy rewritten so AI can easily pick it up",
        "Short, clear answers at the top of every page",
        "FAQ blocks AI can quote directly",
        "Monthly report: where you're mentioned and how",
      ],
    },
    approach: {
      nl: [
        { title: "Nulmeting", desc: "We kijken waar je nu staat in ChatGPT, Perplexity en Google AI Overviews op de vragen die jouw klanten stellen." },
        { title: "Vraag = kop, antwoord = eerste zin", desc: "Elke pagina begint met de vraag en geeft meteen het antwoord. Daarna pas de uitleg." },
        { title: "Feiten in plaats van marketingtaal", desc: "AI citeert duidelijke cijfers en concrete antwoorden, geen vage beloftes. Wij maken je teksten scherper." },
        { title: "Vertrouwen opbouwen", desc: "Auteur, bronnen en interne links laten AI zien dat jouw site betrouwbaar is." },
        { title: "Maandelijks bijsturen", desc: "We meten waar je genoemd wordt en passen content aan waar je nog gemist wordt." },
      ],
      en: [
        { title: "Baseline", desc: "We check where you stand today in ChatGPT, Perplexity and Google AI Overviews on the questions your customers ask." },
        { title: "Question = heading, answer = first line", desc: "Every page opens with the question and gives the answer right away. Context comes after." },
        { title: "Facts instead of marketing talk", desc: "AI quotes clear numbers and concrete answers, not vague promises. We sharpen your copy." },
        { title: "Build trust", desc: "Author, sources and internal links show AI your site is reliable." },
        { title: "Adjust monthly", desc: "We track where you're mentioned and tweak content where you're still missed." },
      ],
    },
    glossary: [
      { term: "AI SEO / GEO", def: { nl: "Generative Engine Optimization. Content zó structureren dat AI-systemen zoals ChatGPT, Perplexity en Google AI Overviews je kunnen vinden, vertrouwen en citeren.", en: "Generative Engine Optimization. Structuring content so AI systems like ChatGPT, Perplexity and Google AI Overviews can find, trust and cite you." } },
      { term: "Citation-ready", def: { nl: "Pagina's met korte, parseerbare blokken, feit-dicht en met duidelijke bronnen, geoptimaliseerd om door AI's letterlijk aangehaald te worden.", en: "Pages with short, parseable, fact-dense blocks and clear sources, optimised to be quoted verbatim by AI systems." } },
      { term: "FAQ schema", def: { nl: "Structured data die expliciet vraag-en-antwoordparen markeert, waardoor zowel Google als LLM's de inhoud direct kunnen extraheren.", en: "Structured data explicitly marking question-and-answer pairs, so both Google and LLMs can extract content directly." } },
    ],
    faq: [
      {
        q: { nl: "Wat is AI SEO of GEO?", en: "What is AI SEO or GEO?" },
        a: {
          nl: "Generative Engine Optimization: content zó structureren dat AI-systemen zoals ChatGPT, Perplexity en Google AI Overviews jouw merk kunnen vinden, vertrouwen en citeren.",
          en: "Generative Engine Optimization: structuring content so AI systems like ChatGPT, Perplexity and Google AI Overviews can find, trust and cite your brand.",
        },
      },
      {
        q: { nl: "In welke AI-engines maken jullie ons zichtbaar?", en: "Which AI engines do you make us visible in?" },
        a: {
          nl: "ChatGPT, Perplexity en Google AI Overviews. We doen een nulmeting op de vragen die jouw klanten daar nu al stellen.",
          en: "ChatGPT, Perplexity and Google AI Overviews. We baseline against the questions your customers are already asking in those engines.",
        },
      },
      {
        q: { nl: "Hoe wordt mijn content citation-ready?", en: "How does my content become citation-ready?" },
        a: {
          nl: "Vraag = kop, antwoord = eerste zin. Daarna pas de uitleg. Feiten en concrete cijfers in plaats van marketingtaal. Auteur, bronnen en interne links om AI te laten zien dat de site betrouwbaar is. FAQ-blokken die direct geciteerd kunnen worden.",
          en: "Question = heading, answer = first line. Context comes after. Facts and concrete numbers instead of marketing talk. Author, sources and internal links to show AI the site is reliable. FAQ blocks that can be quoted directly.",
        },
      },
      {
        q: { nl: "Hoe meten jullie of het werkt?", en: "How do you measure whether it works?" },
        a: {
          nl: "Een maandelijks rapport waar én hoe je wordt genoemd in ChatGPT, Perplexity en AI Overviews. Content wordt bijgestuurd op de vragen waar je nog gemist wordt.",
          en: "A monthly report showing where and how you're mentioned in ChatGPT, Perplexity and AI Overviews. Content is adjusted on the questions where you're still missed.",
        },
      },
    ],
    meta: {
      title: {
        nl: "AI SEO (GEO), zichtbaar in ChatGPT & Perplexity | A-WAVE",
        en: "AI SEO (GEO), visible in ChatGPT & Perplexity | A-WAVE",
      },
      description: {
        nl: "AI SEO / Generative Engine Optimization: content structureren zodat ChatGPT, Perplexity en Google AI Overviews jouw merk vinden, vertrouwen en citeren.",
        en: "AI SEO / Generative Engine Optimization: structure content so ChatGPT, Perplexity and Google AI Overviews find, trust and cite your brand.",
      },
    },
  },
];

export const getService = (slug: string) =>
  services.find((s) => s.slug === slug);

export const serviceNavItems = (lang: Lang) =>
  services.map((s) => ({
    slug: s.slug,
    label:
      s.slug === "website"
        ? { nl: "Website", en: "Website" }[lang]
        : s.slug === "google-ads"
        ? { nl: "Google Ads", en: "Google Ads" }[lang]
        : s.slug === "seo"
        ? { nl: "SEO", en: "SEO" }[lang]
        : { nl: "AI SEO", en: "AI SEO" }[lang],
    desc:
      s.slug === "website"
        ? { nl: "Snelle, conversiegerichte sites op lovable.dev", en: "Fast, conversion-focused sites on lovable.dev" }[lang]
        : s.slug === "google-ads"
        ? { nl: "Performance-tiers, conversiewaardes, capacity launcher", en: "Performance tiers, conversion values, capacity launcher" }[lang]
        : s.slug === "seo"
        ? { nl: "90-dagen organisch fundament + stadspagina's", en: "90-day organic foundation + city pages" }[lang]
        : { nl: "Zichtbaar in ChatGPT, Perplexity & AI Overviews", en: "Visible in ChatGPT, Perplexity & AI Overviews" }[lang],
    href: lang === "en" ? s.pathEn : s.pathNl,
  }));
